import { Button, ButtonGroup, ColSplit, elHasSectionMargin, Grid, InputGroup } from '@reapit/elements'
import { InputTextSignature } from '@reapitform/dashboard-ui'
import { IUserProfileData } from '../../../../../api/user-profile'
import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { getUrl, usePlatformUpdate } from '@reapitform/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { PhoneNumberCountryCode, strictInput, yup } from '@reapitform/utils'
import { ObjectSchema } from 'yup'
import { useOrganizationContact } from '../../../../../hooks/use-orgazination-contact'
import { parsePhoneNumber } from 'libphonenumber-js/max'

export interface IPersonalInfomationEditFormProps {
  onSuccess?: () => void
  onClose?: () => void
  onFailure?: () => void
  defaultData?: IUserProfileData
}

const getPersonalInformationValidationSchema = (countryCode?: PhoneNumberCountryCode) =>
  yup.object().shape({
    firstName: yup
      .string()
      .required('First name is a required field')
      .max(100, "First name can't exceed 100 character"),
    lastName: yup.string().required('Last name is a required field').max(100, "Last name can't exceed 100 character"),
    email: yup.string().strictEmailFormat(),
    mobile: yup.string().mobileNumber(countryCode),
  }) as ObjectSchema<IUserProfileData, any, any, ''>

const PersonalInformationEditForm: React.FC<IPersonalInfomationEditFormProps> = ({
  onClose,
  onSuccess,
  defaultData,
}) => {
  const organizationInformation = useOrganizationContact()
  const phoneCountryCode = organizationInformation?.propertyAddress?.twoLetterISORegionName
  const personalInformationValidationSchema = getPersonalInformationValidationSchema(phoneCountryCode)

  const queryClient = useQueryClient()
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<IUserProfileData>({
    defaultValues: defaultData,
    resolver: yupResolver(personalInformationValidationSchema),
  })

  const [submitRequest, isSubmitLoading] = usePlatformUpdate<IUserProfileData, IUserProfileData>({
    path: '/api/User',
    method: 'PUT',
    headers: {},
    successMessage: 'Your personal information successfully updated!',
    isProtectedRoute: false,
  })

  const handleFormSubmission = async (data: IUserProfileData) => {
    const payload: IUserProfileData = {
      ...data,
    }

    if (data.mobile) payload.mobile = parsePhoneNumber(data.mobile, phoneCountryCode).formatInternational()

    const result = await submitRequest(payload)
    const getUserProfileQueryKey = getUrl(`/api/User/${result.referenceID}`)()

    if (result) {
      queryClient.setQueryData([getUserProfileQueryKey], result)
      queryClient.prefetchQuery({ queryKey: [getUserProfileQueryKey] })
      onSuccess?.()
    }
  }

  const firstName: string = watch('firstName')
  const lastName: string = watch('lastName')
  const fullname: string = useMemo(() => {
    if (!lastName) return firstName
    return `${firstName} ${lastName}`
  }, [firstName, lastName])

  return (
    <form onSubmit={handleSubmit(handleFormSubmission)}>
      <Grid className={elHasSectionMargin}>
        <ColSplit>
          <InputGroup
            label="First Name *"
            type="text"
            {...register('firstName')}
            errorMessage={errors.firstName?.message}
            maxLength={100}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="Last Name *"
            type="text"
            {...register('lastName')}
            errorMessage={errors.lastName?.message}
            maxLength={100}
          />
        </ColSplit>
        <ColSplit>
          <InputGroup label="Reply-to Email" type="text" {...register('email')} errorMessage={errors.email?.message} />
        </ColSplit>
        <ColSplit>
          <InputGroup
            label="Mobile"
            type="text"
            {...register('mobile')}
            {...strictInput(/^[0-9+() ]*$/)}
            errorMessage={errors.mobile?.message}
          />
        </ColSplit>
        <ColSplit>
          <InputTextSignature label="Digital Signature" value={fullname} />
        </ColSplit>
      </Grid>
      <ButtonGroup alignment="right">
        <Button intent="neutral" onClick={onClose} disabled={isSubmitLoading}>
          Cancel
        </Button>
        <Button intent="primary" loading={isSubmitLoading} disabled={isSubmitLoading}>
          Save
        </Button>
      </ButtonGroup>
    </form>
  )
}

export default PersonalInformationEditForm
